import React, { useContext, useEffect, useState } from 'react';
import api from '../api';
import { UserContext } from '../UserContext';
import { useTranslation } from '../LanguageContext';
import { StripeIntentForm, usd } from './StripePayment';

/**
 * Wallet top-up on the Payment Intents API.
 *
 * This was the first flow in the product to collect a card in place rather than
 * redirecting to a hosted Checkout page — a brand tops up mid-task, usually with
 * an applicant waiting to be approved, so the page must not be left. Every other
 * payment now works the same way, and the Elements plumbing they share lives in
 * `StripePayment.jsx`.
 *
 * Two round-trips:
 *   1. ask the backend for a PaymentIntent -> client_secret
 *   2. confirm the card with Stripe, then tell the backend to settle it
 *
 * Step 2's confirmation is a courtesy, not the source of truth — the webhook
 * credits the same intent, and both paths are idempotent on the backend. It
 * exists so the balance is already correct when the modal closes.
 */

const PRESETS = [100, 500, 1000, 5000];

/**
 * The whole flow: amount step -> card step -> done.
 * `onFunded(balanceUsd)` fires once the money is in the wallet.
 */
function DepositModal({ onClose, onFunded, minDeposit = 5, maxDeposit = 50000 }) {
    const { t } = useTranslation();
    const { refreshUser } = useContext(UserContext);

    const [amount, setAmount] = useState('100');
    const [config, setConfig] = useState(null);      // { client_secret, deposit, publishable_key }
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(null);          // { balance_usd, note }

    const value = Number(amount) || 0;
    const valid = value >= minDeposit && value <= maxDeposit;

    // Escape closes the sheet, but never mid-payment.
    useEffect(() => {
        const onKey = (event) => { if (event.key === 'Escape' && !config) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [config, onClose]);

    const startPayment = async () => {
        if (!valid || creating) return;
        setCreating(true);
        setError('');
        try {
            const res = await api.post('/api/wallet/deposit/', { amount_usd: value });
            if (!res.data?.client_secret) {
                setError(t('wallet.startError'));
            } else if (!res.data?.publishable_key) {
                // Without a pk_ key the Payment Element cannot mount at all —
                // name the cause rather than rendering an empty box.
                setError(t('wallet.noPublishableKey'));
            } else {
                setConfig(res.data);
            }
        } catch (err) {
            const code = err.response?.data?.error;
            setError(code === 'amount_out_of_range'
                ? t('wallet.amountOutOfRange')
                : (code === 'invalid_amount' ? t('wallet.invalidAmount') : t('wallet.startError')));
        } finally {
            setCreating(false);
        }
    };

    /** Stripe took the money; settle it so the balance on screen is already right. */
    const handlePaid = async (intent) => {
        let data = null;
        let note = '';
        try {
            const res = await api.post('/api/wallet/deposit/confirm/', {
                payment_intent_id: intent.id,
            });
            data = res.data;
        } catch (err) {
            // Charged but not yet reflected — the webhook still lands it, so say
            // so rather than implying the money vanished.
            note = t('wallet.creditPending');
        }
        setDone({ balance_usd: data?.balance_usd, note });
        refreshUser();
        onFunded?.(data?.balance_usd);
        return null;
    };

    return (
        <div className="fixed inset-0 z-[9600] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">

                <div className="flex items-start justify-between px-6 pt-6 pb-3 flex-shrink-0">
                    <div>
                        <h3 className="text-lg font-black text-slate-900">{t('wallet.depositTitle')}</h3>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                            {t('wallet.depositHint')}
                        </p>
                    </div>
                    <button onClick={onClose}
                        className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 transition flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="px-6 pb-6 overflow-y-auto custom-scrollbar">
                    {/* ── Paid ── */}
                    {done ? (
                        <div className="text-center py-4">
                            <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="text-base font-black text-slate-900">{t('wallet.depositDone')}</p>
                            {done.note ? (
                                <p className="text-xs text-amber-600 mt-1 leading-relaxed">{done.note}</p>
                            ) : (
                                <p className="text-sm text-slate-500 mt-1">
                                    {t('wallet.newBalance')}: <strong className="text-slate-800">{usd(done.balance_usd)}</strong>
                                </p>
                            )}
                            <button onClick={onClose} className="btn-primary w-full py-2.5 text-sm mt-5">
                                {t('wallet.close')}
                            </button>
                        </div>

                    /* ── Card step ── */
                    ) : config ? (
                        <StripeIntentForm
                            clientSecret={config.client_secret}
                            publishableKey={config.publishable_key}
                            amountUsd={config.deposit?.amount_usd}
                            amountLabel={t('wallet.amountToPay')}
                            submitLabel={t('wallet.payNow')}
                            cancelLabel={t('campaigns.cancel')}
                            returnPath="/finance"
                            onSucceeded={handlePaid}
                            onCancel={() => setConfig(null)}
                            note={t('wallet.securityNote')}
                        />

                    /* ── Amount step ── */
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="label" htmlFor="deposit-amount">
                                    {t('wallet.amountLabel')}
                                </label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-black text-slate-300 pointer-events-none">$</span>
                                    <input
                                        id="deposit-amount"
                                        type="number"
                                        inputMode="decimal"
                                        min={minDeposit}
                                        max={maxDeposit}
                                        step="1"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        className="w-full text-center text-3xl font-black text-slate-900 border border-slate-200 rounded-2xl py-3 px-10 focus:outline-none focus:border-[#20c9b5] focus:ring-2 focus:ring-[#20c9b5]/20 transition [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                </div>
                                <p className="text-[11px] text-slate-400 mt-1.5 text-center">
                                    {t('wallet.rangeNote')} {usd(minDeposit)} – {usd(maxDeposit)}
                                </p>
                            </div>

                            <div className="grid grid-cols-4 gap-2">
                                {PRESETS.map(preset => (
                                    <button key={preset} type="button" onClick={() => setAmount(String(preset))}
                                        className={`py-2 rounded-xl text-sm font-bold border transition-colors ${value === preset
                                            ? 'border-[#20c9b5] bg-[#e6fcf9] text-[#0f8a7e]'
                                            : 'border-slate-200 bg-white text-slate-600 hover:border-[#20c9b5]/50'}`}>
                                        ${preset >= 1000 ? `${preset / 1000}k` : preset}
                                    </button>
                                ))}
                            </div>

                            {error && (
                                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                                    {error}
                                </p>
                            )}

                            <button type="button" onClick={startPayment} disabled={!valid || creating}
                                className="btn-primary w-full py-3 text-base font-bold flex items-center justify-center gap-2 disabled:opacity-40">
                                {creating && <span className="spinner-sm" />}
                                {t('wallet.continueToPayment')}
                            </button>

                            <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                                {t('wallet.walletExplain')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DepositModal;
